import React from 'react'
import PropTypes from 'prop-types'
import {
  styled,
  Media,
  Link,
  Image,
  Scroller,
  Title,
  ThemeProvider,
  Box,
  CMSContent,
  ExpandableContent,
  DescriptionList,
  Placeholder,
  Level,
  ErrorBoundary,
  Rating,
  MediaQuery,
  Text,
  Grid,
  Icon,
} from '@deseretbook/react-ui'
import { useMediaQuery } from '@deseretbook/react-utils'
import { DeseretBookCMS, Logger, Analytics } from '../../dbcom'
import { ClickableContent } from '../core'
import { DeseretBook } from '../../dbcom/api'
import { trackAlgoliaEvent } from '../core/search'
import ProductSlices from './ProductSlices'
import {
  ReviewsFilter,
  ReviewsOverview,
  ReviewsList,
  useReviewFilter,
  MostHelpful,
  StarOnlyRating,
} from './reviews'

const logger = new Logger({ context: 'product/MainContent' })

const StyledProfilePicture = styled('div')(({ theme }) => ({
  label: 'ProfilePicture',
  ...theme.typography.large,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '5000px',
  width: theme.spacing.use(3),
  height: theme.spacing.use(3),
  background: theme.palette.secondary.main,
  color: theme.palette.secondary.mainText,
}))

/**
 * Producer profile picture. We could eventually make this usable for any user.
 *
 * @param {object} props - Component props
 * @returns {function} Component
 */
const ProfilePicture = ({ name, src }) => {
  if (src && !src.includes('default_taxon.png')) {
    return (
      <Box width={3} height={3}>
        <Image src={src} aria-hidden imageWidth={3} imageHeight={3} isRounded />
      </Box>
    )
  }

  const initials = name
    .replace(/[^a-z\s+]+/gi, '')
    .toUpperCase()
    .split(' ')
    .map((part) => part[0])

  return <StyledProfilePicture>{[initials.shift(), initials.pop()].join('')}</StyledProfilePicture>
}

ProfilePicture.propTypes = {
  name: PropTypes.string,
  src: PropTypes.string,
}

ProfilePicture.defaultProps = {
  name: '',
  src: '',
}

/**
 * Manages the main content of the product page.
 *
 * @param {object} params - Component params
 * TODO - Add verifiedPurchaser param as part of the product info
 * @param {boolean} verifiedPurchaser - If the user has purchased the product
 * @returns {function} Component
 */
const MainContent = ({ productId, verifiedPurchaser = false }) => {
  const [isLoading, setLoading] = React.useState(true)
  const [isLoadingReviews, setLoadingReviews] = React.useState(true)
  const [product, setProduct] = React.useState()
  const [productReviews, setProductReviews] = React.useState()
  const [cmsDocument, setCmsDocument] = React.useState()
  const [activeVariantId, setActiveVariantId] = React.useState()
  const [filteredReviews, setFilters, filters] = useReviewFilter(
    !isLoadingReviews && productReviews && productReviews.reviews,
  )

  // For when the content reaches its mobile breakpoint
  const isMobile = useMediaQuery({ maxWidth: 639 })
  // For when content reaches mobile widths but isn't technically mobile
  const isNarrow = useMediaQuery({ minWidth: 768, maxWidth: 994 })

  /**
   * Initializes data needed for product page from external sources.
   */
  const initializeData = async () => {
    const productRequest = DeseretBook.product({ id: productId }).get()
    const cmsRequest = DeseretBookCMS.getByUID('product_v1', String(productId))

    productRequest.catch(({ message }) => {
      logger.error(`Unable to fetch product: "${message}"`)
    })

    cmsRequest.catch(({ message }) => {
      logger.debug(`Unable to fetch product cms document: "${message}"`)
    })

    const [productResponse, cmsResponse] = await Promise.all([productRequest, cmsRequest])

    if (cmsResponse && cmsResponse.data && cmsResponse.data.theme) {
      try {
        const theme = await DeseretBookCMS.themes().getByID(cmsResponse.data.theme.id)

        cmsResponse.data.theme = theme
      } catch ({ message }) {
        logger.debug(`Unable to fetch theme cms document: "${message}"`)
      }
    }

    setProduct(productResponse)
    setCmsDocument(cmsResponse.data)
    setLoading(false)
  }

  /**
   * Gets the Products reviews once product details is finished
   */
  const getReviews = async () => {
    try {
      const productReviewsResponse = await DeseretBook.product({
        id: productId,
      })
        .review()
        .all()

      setProductReviews(productReviewsResponse)
      setLoadingReviews(false)
    } catch (message) {
      logger.error(`Unable to fetch product reviews: "${message}"`)
    }
  }

  /**
   * Subscriber for page context updates.
   *
   * @param {string} action - Name of action
   * @param {string} data - Data related to action
   */
  const subscriber = (action, data) => {
    // We only care about changes to the variant here.
    if (action !== 'VARIANT' || String(data.variantId) === activeVariantId) {
      return
    }

    setActiveVariantId(String(data.variantId))
  }

  /**
   * Tracks the product view with Algolia
   */
  const trackProductView = () => {
    const { searchParams } = new URL(window.location.href)
    const queryID = searchParams.get('queryID')

    trackAlgoliaEvent('viewedObjectIDs', {
      eventName: 'Product Viewed',
      objectIDs: [String(productId)],
      ...(queryID && { queryID }),
    })
  }

  /**
   * When first initialized we want to retrieve relevant CMS data, as well as subscribe to the page
   * context so we know if and when the current variant changes so we can update accordingly.
   */
  React.useEffect(() => {
    initializeData()
    getReviews()

    trackProductView()

    if (window.contextObserver) {
      window.contextObserver.subscribe(subscriber)

      // Now that we're subscribed, we'll fetch the current variant data to see if one has already
      // been selected.
      const variantData = window.contextObserver.get('VARIANT')

      if (variantData && variantData.variantId) {
        setActiveVariantId(String(variantData.variantId))
      }
    }

    return () => {
      if (window.contextObserver) {
        window.contextObserver.unsubscribe(subscriber)
      }
    }
  }, [])

  // We'll bail out if we were unable to fetch the product.
  if (!isLoading && !product) {
    return false
  }

  // This is the actively selected variant.
  const variant = product && product.variants.find(({ id }) => String(id) === activeVariantId)

  return (
    <ThemeProvider theme={(cmsDocument && cmsDocument.theme) || {}}>
      <React.Fragment>
        {/* CMS Product Information Content */}
        {!!(cmsDocument && cmsDocument.productInformationSlices.length) && (
          <Box marginVertical={2}>
            <ProductSlices
              slices={cmsDocument && cmsDocument.productInformationSlices}
              variant={activeVariantId}
            />
          </Box>
        )}

        {/* Product Description */}
        {!!(isLoading || (product && product.description)) && (
          <Box marginVertical={2}>
            <Title size={2} titleProps={{ as: 'h2' }} title="Description" />
            {isLoading ? (
              <Placeholder height={10}>
                <Placeholder.Text lines={6} />
              </Placeholder>
            ) : (
              <ExpandableContent
                collapsedHeight={10}
                onExpand={() => {
                  Analytics.event(
                    {
                      category: 'Product Page',
                      action: 'User Expanded Product Description',
                    },
                    { isInteractive: true },
                  )
                }}
                onCollapse={() => {
                  Analytics.event(
                    {
                      category: 'Product Page',
                      action: 'User Collapsed Product Description',
                    },
                    { isInteractive: true },
                  )
                }}
              >
                <CMSContent
                  itsNotDangerousBecause="It is dangerous. Fix this ASAP!!!!!"
                  dangerouslySetInnerHTML={{
                    __html: product.description,
                  }}
                />

                <CMSContent
                  itsNotDangerousBecause="It is dangerous. Fix this ASAP!!!!!"
                  dangerouslySetInnerHTML={{
                    __html: product.additionalDetails,
                  }}
                />
              </ExpandableContent>
            )}
          </Box>
        )}

        {/* Product / Variant Details List */}
        {!!(
          isLoading ||
          (product && product.productProperties && product.productProperties.length)
        ) && (
          <Box marginVertical={2}>
            <Title size={2} titleProps={{ as: 'h2' }} title="Details" />
            {isLoading ? (
              <Placeholder height={10} maxWidth={30}>
                <DescriptionList>
                  {[1, 2, 3, 4].map((term) => (
                    <React.Fragment key={term}>
                      <DescriptionList.Term>
                        <Box width={5}>
                          <Placeholder.Text lines={1} />
                        </Box>
                      </DescriptionList.Term>
                      <DescriptionList.Description>
                        <Placeholder.Text lines={1} />
                      </DescriptionList.Description>
                    </React.Fragment>
                  ))}
                </DescriptionList>
              </Placeholder>
            ) : (
              <ExpandableContent
                collapsedHeight={10}
                onExpand={() => {
                  Analytics.event(
                    {
                      category: 'Product Page',
                      action: 'User Expanded Product Properties',
                    },
                    { isInteractive: true },
                  )
                }}
                onCollapse={() => {
                  Analytics.event(
                    {
                      category: 'Product Page',
                      action: 'User Collapsed Product Properties',
                    },
                    { isInteractive: true },
                  )
                }}
              >
                <DescriptionList>
                  {!!(variant && variant.sku) && (
                    <React.Fragment>
                      <DescriptionList.Term>SKU</DescriptionList.Term>
                      <DescriptionList.Description>{variant.sku}</DescriptionList.Description>
                    </React.Fragment>
                  )}
                  {((variant && variant.optionValues) || []).map((option) => (
                    <React.Fragment key={option.optionTypePresentation}>
                      <DescriptionList.Term>{option.optionTypePresentation}</DescriptionList.Term>
                      <DescriptionList.Description>
                        {option.presentation}
                      </DescriptionList.Description>
                    </React.Fragment>
                  ))}
                  {product.productProperties.map((productProperty) => (
                    <React.Fragment key={productProperty.id}>
                      <DescriptionList.Term>{productProperty.propertyName}</DescriptionList.Term>
                      <DescriptionList.Description>
                        {productProperty.value}
                      </DescriptionList.Description>
                    </React.Fragment>
                  ))}
                </DescriptionList>
              </ExpandableContent>
            )}
          </Box>
        )}

        {/* List of Producers */}
        {!!(isLoading || (product && product.producers.length)) && (
          <Box marginVertical={2}>
            {isLoading ? (
              <Placeholder width={15} padding={0.5}>
                <Media>
                  <Media.Figure>
                    <Placeholder.Block width={3} height={3} />
                  </Media.Figure>
                  <Media.Content>
                    <Placeholder.Text lines={2} />
                  </Media.Content>
                </Media>
              </Placeholder>
            ) : (
              <Scroller>
                {product.producers.map((producer) => (
                  <Scroller.Item key={producer.id}>
                    <Box width={15}>
                      <ClickableContent
                        as={Link}
                        href={`/t/${producer.permalink}?nav_source=producer_list`}
                      >
                        <Media>
                          <Media.Figure>
                            <ProfilePicture name={producer.name} src={producer.icon} />
                          </Media.Figure>
                          <Media.Content>
                            <Title
                              titleProps={{ as: 'h4' }}
                              title={producer.name}
                              subtitle="View Products"
                            />
                          </Media.Content>
                        </Media>
                      </ClickableContent>
                    </Box>
                  </Scroller.Item>
                ))}
              </Scroller>
            )}
          </Box>
        )}

        {/* Reviews Overview */}
        <Box id="reviews" maxWidth={32}>
          <ErrorBoundary failsSilently>
            <Title title="Reviews" size={2} titleProps={{ as: 'h2' }} />
            {!!(isLoadingReviews || (product && productReviews)) &&
              (isLoadingReviews ? (
                <MediaQuery.Medium>
                  {(isMediumOrLarger) => (
                    <Placeholder>
                      <Level>
                        <Level.Left>
                          <Level.Item>
                            <Box
                              {...(isMediumOrLarger
                                ? {
                                    width: 14,
                                    alignItems: 'center',
                                    isColumn: true,
                                  }
                                : {
                                    minWidth: 6,
                                    maxWidth: 14,
                                    isFullwidth: true,
                                  })}
                            >
                              <Box
                                paddingBotton={1}
                                alignItems="center"
                                isColumn={!isMediumOrLarger}
                              >
                                <Text color="disabled" isBold size="xLarge">
                                  0.0
                                </Text>
                                <Box
                                  isColumn
                                  alignItems={isMediumOrLarger ? 'start' : 'center'}
                                  marginLeft={isMediumOrLarger && 0.5}
                                >
                                  {isMediumOrLarger && <Rating average={5} />}
                                  <Text color="disabled" size="small">
                                    0 Reviews
                                  </Text>
                                </Box>
                              </Box>
                              {isMediumOrLarger && (
                                <Box height={2.5} width={9.5} marginHorizontal={0.25} />
                              )}
                            </Box>
                          </Level.Item>
                        </Level.Left>
                        <Level.Right isShrinkable>
                          <Level.Item>
                            <Box flexGrow={1} width={20}>
                              {[5, 4, 3, 2, 1].map((starRating) => (
                                <Level key={starRating}>
                                  <Level.Left>
                                    <Level.Item>
                                      <Box width={3}>
                                        <Rating average={starRating} type="minimal" />
                                      </Box>
                                    </Level.Item>
                                  </Level.Left>
                                  <Level.Item>
                                    <Placeholder.Block width="100%" height={0.25} />
                                  </Level.Item>
                                  <Level.Right>
                                    <Level.Item>
                                      <Box width={2}>
                                        <Text color="disabled" hasEllipsis alignment="right">
                                          0
                                        </Text>
                                      </Box>
                                    </Level.Item>
                                  </Level.Right>
                                </Level>
                              ))}
                            </Box>
                          </Level.Item>
                        </Level.Right>
                      </Level>
                      <Box marginTop={2}>
                        <Placeholder.Block />
                      </Box>
                    </Placeholder>
                  )}
                </MediaQuery.Medium>
              ) : (
                <ReviewsOverview
                  productId={productId}
                  reviews={productReviews.reviews}
                  avgRating={productReviews.avgRating}
                  setFilters={setFilters}
                />
              ))}
          </ErrorBoundary>
        </Box>

        {/* Star only rating */}
        <ErrorBoundary failsSilently>
          {!!(isLoadingReviews || (product && productReviews)) &&
            (isLoadingReviews ? (
              <Placeholder>
                <Box marginVertical={2} padding={1} maxWidth={isMobile ? '100%' : 45}>
                  <Grid
                    alignItems="center"
                    justifyItems="left"
                    breakpoints={{
                      '100': {
                        areas: ['title', 'rating', 'text', 'link'],
                        columns: Grid.fr(1),
                        rows: [2, 4, Grid.fr(1), Grid.minMax(Grid.minContent, Grid.maxContent)],
                        justifyItems: 'center',
                      },
                      '640': {
                        areas: ['title rating link', 'text text text'],
                        columns: [
                          Grid.minMax(Grid.minContent, Grid.maxContent),
                          Grid.fr(1),
                          Grid.minMax(Grid.minContent, Grid.maxContent),
                        ],
                        columnGap: 1,
                        rows: [3, Grid.fr(1)],
                        justifyItems: 'left',
                      },
                      '768': {
                        areas: ['title', 'rating', 'text', 'link'],
                        columns: Grid.fr(1),
                        rows: [2, 4, Grid.fr(1), Grid.minMax(Grid.minContent, Grid.maxContent)],
                        justifyItems: 'center',
                      },
                      '995': {
                        areas: ['title rating link', 'text text text'],
                        columns: [
                          Grid.minMax(Grid.minContent, Grid.maxContent),
                          Grid.fr(1),
                          Grid.minMax(Grid.minContent, Grid.maxContent),
                        ],
                        columnGap: 1,
                        rows: [3, Grid.fr(1)],
                        justifyItems: 'left',
                      },
                    }}
                  >
                    <Grid.Item area="title">
                      <Placeholder.Block height={1.5} width={10} alignItems="center" />
                    </Grid.Item>
                    <Grid.Item area="rating">
                      <Box alignItems="center">
                        <Rating size={isMobile ? 'large' : 'medium'} average={5} />
                      </Box>
                    </Grid.Item>
                    <Grid.Item area="link" justify={isMobile || isNarrow ? 'center' : 'right'}>
                      <Placeholder.Block height={2} width={6} />
                    </Grid.Item>
                    <Grid.Item area="text">
                      <Box marginVertical={0.5}>
                        <Placeholder.Block height={1} width={20} />
                        <Placeholder.Block marginTop={1} height={1} width={10} />
                      </Box>
                    </Grid.Item>
                  </Grid>
                </Box>
              </Placeholder>
            ) : (
              <StarOnlyRating
                productId={product.id}
                productName={product.name}
                verifiedPurchaser={verifiedPurchaser}
                isMobile={isMobile}
                isNarrow={isNarrow}
              />
            ))}
        </ErrorBoundary>

        {/* Most Helpful Positive/Negative Reviews */}
        <ErrorBoundary failsSilently>
          {!!(isLoadingReviews || (product && productReviews)) &&
            (isLoadingReviews ? (
              <Placeholder>
                {[5, 1].map((rating) => (
                  <Box marginVertical={1.5}>
                    <Level>
                      <Level.Left>
                        <Level.Item>
                          <Icon
                            name={rating === 5 ? 'thumbUp' : 'thumbDown'}
                            shade="light"
                            color="secondary"
                          />
                        </Level.Item>
                        <Level.Item>
                          <Title
                            title={`Most helpful ${rating === 5 ? 'positive' : 'critical'} review`}
                            titleAsComponent="h3"
                            subtitle="Customers found this helpful"
                          />
                        </Level.Item>
                      </Level.Left>
                    </Level>
                    <Box>
                      <Rating average={rating} size="small" />
                      <Placeholder.Block isInline marginLeft={0.5} width={10} height={1} />
                      <Placeholder.Block width={12} height={1} marginBottom={1} marginTop={0.5} />
                      <Placeholder.Text lines={3} />
                    </Box>
                  </Box>
                ))}
              </Placeholder>
            ) : (
              <Box marginVertical={2}>
                <MostHelpful reviews={productReviews.reviews} />
              </Box>
            ))}
        </ErrorBoundary>

        {/* Reviews Filter */}
        <ErrorBoundary failsSilently>
          {!!(isLoadingReviews || (product && productReviews)) &&
            (isLoadingReviews ? (
              <div />
            ) : (
              <Box marginTop={1} maxWidth={32}>
                <ReviewsFilter setFilters={setFilters} filters={filters} />
              </Box>
            ))}
        </ErrorBoundary>

        {/* Reviews List */}
        {!!(isLoadingReviews || (product && productReviews)) && (
          <Box paddingVertical={2}>
            {isLoadingReviews ? (
              <Placeholder>
                {[1, 2, 3].map((num) => (
                  <Box key={num} marginBottom={1}>
                    <Rating average="5" size="small" />
                    <Placeholder.Block isInline marginLeft={0.5} width={10} height={1} />
                    <Placeholder.Block width={12} height={1} marginBottom={1} marginTop={0.5} />
                    <Placeholder.Text lines={3} />
                  </Box>
                ))}
              </Placeholder>
            ) : (
              <ReviewsList
                filters={filters}
                setFilters={setFilters}
                total={productReviews.reviews.length}
                reviews={filteredReviews}
                productId={productId}
              />
            )}
          </Box>
        )}
      </React.Fragment>
    </ThemeProvider>
  )
}

MainContent.propTypes = {
  productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  verifiedPurchaser: PropTypes.bool,
}

export default MainContent
